import { useState, useMemo, useCallback, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// mantine
import { AppShell, Box } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

// project imports
import PortalProtector from './AuthProtector';
import Header from './Header';
import Sidebar from './Sidebar';
import DrawerContext from 'contexts/DrawerContext';
import AIFlightDealsContext from 'contexts/AIFlightDealsContext';

import KayakDialog from 'ui-components/dialogs/KayakDialog';
import AISearchCheapFlightDialog from 'ui-components/dialogs/AISearchCheapFlightDialog';
import FloatingActionMenu from 'ui-components/buttons/FloatingActionMenu';
import useNotificationPolling from 'hooks/use-notification-polling';
import { semanticColors, motion, zIndex as zIndexTokens } from 'theme';

const drawerWidth = 260;
const collapseDrawerWidth = 100;

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const matchDownLg = useMediaQuery('(max-width: 75em)');
  const { pathname } = useLocation();
  useNotificationPolling();

  // Handle left drawer
  const [leftDrawerOpened, setLeftDrawerOpened] = useState(!matchDownLg);
  const [isLoading, setIsLoading] = useState(false);
  const [openKayakDialog, setOpenKayakDialog] = useState(false);
  const [openAIDialog, setOpenAIDialog] = useState(false);
  const [dealsRefreshKey, setDealsRefreshKey] = useState(0);

  // Mobile navigation opened state (separate from desktop collapse/expand)
  const [mobileOpened, setMobileOpened] = useState(false);

  // Scroll detection for mobile AppBar
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleLeftDrawerToggle = useCallback(() => {
    if (matchDownLg) {
      setMobileOpened((prev) => !prev);
    } else {
      setLeftDrawerOpened((prev) => !prev);
    }
  }, [matchDownLg]);

  // Close mobile nav when switching to desktop
  useEffect(() => {
    if (!matchDownLg) {
      setMobileOpened(false);
    }
  }, [matchDownLg]);

  // Close mobile nav after any navigation (nav items, logo, programmatic).
  // The AppShell collapse transition animates the close.
  useEffect(() => {
    setMobileOpened(false);
  }, [pathname]);

  // Lock body scroll while the mobile nav is open. AppShell.Navbar has no
  // overlay/scroll-lock like a Drawer, so without this the page behind the
  // menu is what scrolls (scroll-chaining) instead of the menu itself.
  useEffect(() => {
    if (!(matchDownLg && mobileOpened)) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [matchDownLg, mobileOpened]);

  // Scroll detection effect (only for mobile)
  useEffect(() => {
    if (!matchDownLg) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when scrolling up or at the top
      if (currentScrollY < lastScrollY || currentScrollY < 10) {
        setIsHeaderVisible(true);
      }
      // Hide header when scrolling down
      else if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setIsHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, matchDownLg]);

  const contextValue = useMemo(
    () => ({
      leftDrawerOpened,
      setLeftDrawerOpened,
      isLoading,
      setIsLoading
    }),
    [leftDrawerOpened, setLeftDrawerOpened, isLoading, setIsLoading]
  );

  const dealsContextValue = useMemo(
    () => ({
      refreshKey: dealsRefreshKey,
      triggerRefresh: () => setDealsRefreshKey((prev) => prev + 1)
    }),
    [dealsRefreshKey]
  );

  return (
    <PortalProtector>
      <DrawerContext.Provider value={contextValue}>
        <AppShell
          header={{ height: { base: 56, md: 80 } }}
          navbar={{
            width: {
              base: drawerWidth,
              lg: leftDrawerOpened ? drawerWidth : collapseDrawerWidth
            },
            breakpoint: 'lg',
            collapsed: { mobile: !mobileOpened }
          }}
          padding="md"
          transitionDuration={200}
          transitionTimingFunction={motion.easing.standard}
          styles={{
            root: {
              minHeight: '100vh'
            },
            header: {
              backgroundColor: semanticColors.surface.base,
              borderBottom: '1.5px solid var(--mantine-color-coral-6)',
              zIndex: zIndexTokens.sticky,
              // Mobile scroll-hide behavior
              ...(matchDownLg && {
                transform: isHeaderVisible ? 'translateY(0)' : 'translateY(-100%)',
                transition: `transform ${motion.duration.standard} ease-in-out`
              })
            },
            navbar: {
              backgroundColor: semanticColors.surface.base,
              borderRight: `1px solid ${semanticColors.border.default}`,
              transition: `width ${motion.duration.standard} ${motion.easing.standard}`
            },
            main: {
              backgroundColor: semanticColors.surface.base,
              minHeight: '100vh'
            }
          }}
        >
          <AppShell.Header>
            <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
          </AppShell.Header>

          <AppShell.Navbar>
            <Sidebar drawerOpen={matchDownLg ? true : leftDrawerOpened} />
          </AppShell.Navbar>

          <AppShell.Main>
            <AIFlightDealsContext.Provider value={dealsContextValue}>
              <Outlet />
              <FloatingActionMenu onOpenAIDialog={() => setOpenAIDialog(true)} onOpenKayakDialog={() => setOpenKayakDialog(true)} />
              <AISearchCheapFlightDialog open={openAIDialog} onClose={() => setOpenAIDialog(false)} />
              <KayakDialog open={openKayakDialog} onClose={() => setOpenKayakDialog(false)} />
            </AIFlightDealsContext.Provider>
          </AppShell.Main>
        </AppShell>
      </DrawerContext.Provider>
    </PortalProtector>
  );
};

export default MainLayout;
